import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { isEntraAuth, msalInstance } from '../../auth/authConfig';
import { databasesApi, kpiApi } from '../../services/api';
import splashVideo from '../../assets/dawn_splash_2.mp4';
import wmLogo from '../../assets/logos/WM-Logo-CB-White-Header.png';
import { CheckCircle, Loader, Bug } from 'lucide-react';

const ALL_STEPS = [
  { key: 'connecting',     label: 'Connecting to Microsoft' },
  { key: 'authenticating', label: 'Signing you in'          },
  { key: 'verifying',      label: 'Checking your account'   },
  { key: 'selecting',      label: 'Select a database'       },
  { key: 'loading',        label: 'Loading your workspace'  },
];
const STEP_ORDER = ALL_STEPS.map(s => s.key);

const AuthGate = ({ children }) => {
  const location   = useLocation();
  const navigate   = useNavigate();
  const { authStep, currentUser } = useAuth();

  const isInvitePage  = location.pathname.startsWith('/accept-invitation');
  const alreadyShown  = sessionStorage.getItem('dawn-splash-shown') === 'true';
  const noAccountsYet = isEntraAuth() && msalInstance && msalInstance.getAllAccounts().length === 0;
  const skipVideo     = isInvitePage || alreadyShown || noAccountsYet;

  const [videoPhase,  setVideoPhase]  = useState(!skipVideo);
  const [localPhase,  setLocalPhase]  = useState('auth'); // 'auth' | 'selecting' | 'loading'
  const [databases,   setDatabases]   = useState([]);
  const [gateExiting, setGateExiting] = useState(false);
  const [gateDone,    setGateDone]    = useState(false);
  const [debugMode,   setDebugMode]   = useState(false);
  const [, forceUpdate] = useState(0);

  const startTimeRef  = useRef(Date.now());
  const logRef        = useRef([]);
  const debugModeRef  = useRef(false);

  const addLog = useCallback((msg, type = 'info') => {
    const ms      = Date.now() - startTimeRef.current;
    const sec     = Math.floor(ms / 1000);
    const frac    = String(ms % 1000).padStart(3, '0');
    const elapsed = `[${String(sec).padStart(2, '0')}:${frac}]`;
    logRef.current = [...logRef.current, { time: elapsed, msg, type }];
    if (debugModeRef.current) forceUpdate(n => n + 1);
  }, []);

  const toggleDebug = () => {
    const next = !debugMode;
    debugModeRef.current = next;
    setDebugMode(next);
    if (next) forceUpdate(n => n + 1);
  };

  const exitGate = useCallback(() => {
    addLog('Gate complete', 'step');
    setGateExiting(true);
    setTimeout(() => setGateDone(true), 500);
  }, [addLog]);

  // Log authStep transitions
  const prevAuthStep = useRef(null);
  useEffect(() => {
    if (authStep !== prevAuthStep.current && !gateDone) {
      addLog(`AUTH: ${authStep}`, 'step');
      prevAuthStep.current = authStep;
    }
  }, [authStep, addLog, gateDone]);

  // --- Video phase ---
  const completeVideo = useCallback(() => {
    sessionStorage.setItem('dawn-splash-shown', 'true');
    setVideoPhase(false);
  }, []);

  useEffect(() => {
    if (!videoPhase) return;
    const t = setTimeout(completeVideo, 60000);
    return () => clearTimeout(t);
  }, [videoPhase, completeVideo]);

  // --- Transition: auth ready → selecting ---
  useEffect(() => {
    if (!videoPhase && authStep === 'ready' && currentUser && localPhase === 'auth' && !gateExiting) {
      addLog('Auth resolved → selecting database', 'step');
      setLocalPhase('selecting');
    }
  }, [videoPhase, authStep, currentUser, localPhase, gateExiting, addLog]);

  // --- Step 4: fetch DB list ---
  const handleSelectDb = useCallback((db) => {
    // URL slug now comes from db.slug (the Slug column). Falls back to display-name-lowercased
    // for old DBs that don't have a slug populated yet (shouldn't happen post-migration).
    const slug = db.slug || (db.displayName || db.name).toLowerCase();
    const name = db.databaseName || db.displayName || db.name;
    localStorage.setItem('pmd-selected-db-slug', slug);
    localStorage.setItem('pmd-selected-database', name);
    addLog(`DB selected: ${db.displayName || db.name} → /${slug}/`, 'step');
    navigate(`/${slug}/`);
    setLocalPhase('loading');
  }, [addLog, navigate]);

  useEffect(() => {
    if (localPhase !== 'selecting') return;
    addLog('API: databases/available → started');
    const t0 = Date.now();
    databasesApi.getAvailable()
      .then(res => {
        const dbs = res.data || [];
        addLog(`API: databases/available → OK (${Date.now() - t0}ms), ${dbs.length} DB(s)`);
        setDatabases(dbs);
        if (dbs.length === 1) handleSelectDb(dbs[0]);
      })
      .catch(err => {
        addLog(`API: databases/available → ERROR: ${err.message}`, 'error');
        setDatabases([]);
      });
  }, [localPhase, handleSelectDb, addLog]);

  // --- Step 5: KPI pre-fetch (soft gate, 8s timeout) ---
  useEffect(() => {
    if (localPhase !== 'loading') return;
    addLog('API: kpi/home → started');
    const t0 = Date.now();
    let settled = false;
    const settle = (msg, type = 'info') => {
      if (settled) return;
      settled = true;
      addLog(msg, type);
      exitGate();
    };
    const timeout = setTimeout(
      () => settle('API: kpi/home → 8s timeout (soft gate — proceeding)', 'error'),
      8000
    );
    kpiApi.getHome()
      .then(() => { clearTimeout(timeout); settle(`API: kpi/home → OK (${Date.now() - t0}ms)`); })
      .catch(err => { clearTimeout(timeout); settle(`API: kpi/home → ERROR: ${err.message}`, 'error'); });
    return () => clearTimeout(timeout);
  }, [localPhase, exitGate, addLog]);

  // Combined display step for step indicators
  const displayStep    = localPhase === 'selecting' ? 'selecting'
    : localPhase === 'loading' ? 'loading'
    : (authStep === 'ready' ? 'verifying' : authStep);
  const currentStepIdx = STEP_ORDER.indexOf(displayStep);

  if (isInvitePage) return children;
  if (gateDone)     return children;

  const showDbList = localPhase === 'selecting' && databases.length > 1;

  return (
    <>
      {gateExiting && children}
      <div className={`auth-gate-backdrop${gateExiting ? ' auth-gate-exiting' : ''}`}>
        <div className="auth-gate-card" style={{ position: 'relative' }}>

          {videoPhase ? (
            <video
              className="auth-gate-video"
              autoPlay muted playsInline
              onCanPlay={e => { e.target.playbackRate = 2; }}
              onEnded={completeVideo}
            >
              <source src={splashVideo} type="video/mp4" />
            </video>
          ) : (
            <>
              {/* Header */}
              <div className="auth-gate-header">
                <img src={wmLogo} alt="Wunderlich-Malec" className="auth-gate-logo" />
                <div className="auth-gate-header-divider" />
                <div>
                  <div className="auth-gate-title">DAWN</div>
                  <div className="auth-gate-subtitle">DOCUMENTATION · ASSETS · WIRING · NETWORKING</div>
                </div>
              </div>

              {/* Body — DB picker always wins when needed; debug replaces step indicators only */}
              {showDbList ? (
                <>
                  <div className="auth-gate-db-header">Select a database</div>
                  <div className="auth-gate-db-list">
                    {databases.map(db => {
                      const name = db.displayName || db.name;
                      return (
                        <button key={db.id || name} className="auth-gate-db-item" onClick={() => handleSelectDb(db)}>
                          {db.iconUrl ? (
                            <img src={db.iconUrl} alt="" className="auth-gate-db-icon" />
                          ) : (
                            <div className="auth-gate-db-icon-placeholder">{name.charAt(0)}</div>
                          )}
                          <div className="auth-gate-db-info">
                            <div className="auth-gate-db-name">{name}</div>
                            {db.description && <div className="auth-gate-db-desc">{db.description}</div>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : debugMode ? (
                <div className="auth-gate-debug-panel">
                  {logRef.current.length === 0 ? (
                    <div className="auth-gate-debug-entry" style={{ color: '#9ca3af' }}>No events yet</div>
                  ) : (
                    logRef.current.map((entry, i) => (
                      <div
                        key={i}
                        className={`auth-gate-debug-entry${entry.type === 'error' ? ' error' : entry.type === 'step' ? ' step' : ''}`}
                      >
                        {entry.time} {entry.msg}
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <div className="auth-gate-steps">
                  {ALL_STEPS.map((step, idx) => {
                    const isDone   = idx < currentStepIdx;
                    const isActive = !isDone && idx === currentStepIdx;
                    const state    = isDone ? 'done' : isActive ? 'active' : 'pending';
                    return (
                      <div key={step.key} className={`auth-gate-step ${state}`}>
                        <div className="auth-gate-step-icon">
                          {isDone   ? <CheckCircle size={20} color="#1a5568" /> :
                           isActive ? <Loader size={20} className="auth-gate-spinner" color="#1a5568" /> :
                                      <div className="auth-gate-step-circle" />}
                        </div>
                        <span className="auth-gate-step-label">{step.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Debug toggle */}
              <button className="auth-gate-debug-icon" onClick={toggleDebug} title={debugMode ? 'Show steps' : 'Show debug trace'}>
                <Bug size={14} />
              </button>
            </>
          )}

        </div>
      </div>
    </>
  );
};

export default AuthGate;
